import React, { useEffect, useState, useMemo, useRef, useLayoutEffect } from 'react';
import { InputNumber, Button, Switch, Tooltip, message, Popover } from 'antd';
import { PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import s from './s.module.less';
import InsuranceCarrierModal from 'components/InsuranceCarrierModal';
import { EServiceType, InsuranceSetting, ServerKlarityService, TServiceStateItem } from 'types/common';
import { INSURANCE_TYPE_INSURANCE, INSURANCE_TYPE_CASH, INSURANCE_TYPE_CASH_AND_INSURANCE } from 'constants/common';
import { IPPAServicePriceConfigResponse, PPAServicePriceConfig } from 'types/ppaOnboarding';
import ProviderStore from 'store/Provider';
import useKlarityServiceTemplate from 'hooks/useKlarityServiceTemplate';
import { useSwitchedProvider } from 'pages/ReviewProviderProfilePage/SwitchedProviderContext';

interface StateData {
    code: string;
    duration: number;
    cashPrice: number | null;
    cashPay: boolean;
    insurancePay: boolean;
    superBillSupport?: boolean | null;
    priorAuthorizationSupport?: boolean | null;
}

interface ValidationResult {
    isValid: boolean;
    errors: string[];
}

interface InsuranceTableProps {
    isEdit?: boolean;
    selectedSpecialty?: string;
    states?: string[];
    servicePriceConfig?: IPPAServicePriceConfigResponse[] | PPAServicePriceConfig[];
    hideDuration?: boolean;
    className?: string;
    paymentType?:
        | typeof INSURANCE_TYPE_INSURANCE
        | typeof INSURANCE_TYPE_CASH
        | typeof INSURANCE_TYPE_CASH_AND_INSURANCE;
    onAddInsurance?: (stateCode: string) => void;
    onChange?: (data?: ServerKlarityService | null) => void;
    isReview?: boolean;
    initialInsuranceSettings?: Record<
        string,
        InsuranceSetting & {
            duration?: number;
            cashPrice?: number;
            insurancePay?: boolean;
            cashPay?: boolean;
            superBillSupport?: boolean | null;
            priorAuthorizationSupport?: boolean | null;
        }
    >;
    showSuperBillSupport?: boolean;
    showPriorAuthorizationSupport?: boolean;
    isOps?: boolean;
    providerIdForOps?: number; // ops端的时候需要传，provider端不需要，会自动从缓存中读取
    hideDuration?: boolean;
}

const handleValidation = (
    statesData: StateData[],
    insuranceSettings: Record<
        string,
        InsuranceSetting & {
            duration?: number;
            cashPrice?: number;
            insurancePay?: boolean;
            cashPay?: boolean;
        }
    >,
    paymentType: string,
): ValidationResult => {
    const errors: string[] = [];

    // 1. 纯现金支付模式：所有州都需要设置现金价格
    if (paymentType === INSURANCE_TYPE_CASH) {
        const statesWithoutPrice = statesData.filter((state) => !state.cashPrice);
        if (statesWithoutPrice.length > 0) {
            errors.push(
                `Please set cash price for states: ${statesWithoutPrice.map((state) => state.code).join(', ')}`,
            );
        }
    }

    // 2. 纯保险模式：所有州都需要设置保险公司
    if (paymentType === INSURANCE_TYPE_INSURANCE) {
        const statesWithoutCarrier = statesData.filter((state) => {
            return (
                !insuranceSettings[state.code] ||
                !insuranceSettings[state.code].carriers ||
                !insuranceSettings[state.code].carriers.length
            );
        });

        if (statesWithoutCarrier.length > 0) {
            errors.push(
                `Please select insurance carriers for states: ${statesWithoutCarrier
                    .map((state) => state.code)
                    .join(', ')}`,
            );
        }
    }

    // 3. 混合模式：根据每个州的开关状态验证
    if (paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE) {
        // 检查开启了保险的州是否设置了保险公司
        const statesNeedCarrier = statesData.filter(
            (state) =>
                state.insurancePay &&
                (!insuranceSettings[state.code] ||
                    !insuranceSettings[state.code].carriers ||
                    !insuranceSettings[state.code].carriers.length),
        );
        if (statesNeedCarrier.length > 0) {
            errors.push(
                `Please select insurance carriers for states: ${statesNeedCarrier
                    .map((state) => state.code)
                    .join(', ')}`,
            );
        }

        // 检查开启了现金支付的州是否设置了价格
        const statesNeedPrice = statesData.filter((state) => state.cashPay && !state.cashPrice);
        if (statesNeedPrice.length > 0) {
            errors.push(`Please set cash price for states: ${statesNeedPrice.map((state) => state.code).join(', ')}`);
        }

        // 检查是否每个州至少开启了一种支付方式
        const statesWithoutPayment = statesData.filter((state) => !state.cashPay && !state.insurancePay);
        if (statesWithoutPayment.length > 0) {
            errors.push(
                `Please enable at least one payment method for states: ${statesWithoutPayment
                    .map((state) => state.code)
                    .join(', ')}`,
            );
        }
    }

    return {
        isValid: errors.length === 0,
        errors,
    };
};

interface InsuranceTableRef {
    getServerKlarityServiceData: () => ServerKlarityService | null;
}

// 共享 canvas 用于测量文本宽度，避免每次创建
let sharedMeasureCanvas: HTMLCanvasElement | null = null;
const getMeasureCtx = (): CanvasRenderingContext2D | null => {
    if (!sharedMeasureCanvas) {
        sharedMeasureCanvas = document.createElement('canvas');
    }
    return sharedMeasureCanvas.getContext('2d');
};

// 把每个 carrier 拼成一段展示文本：「Carrier: plan1, plan2」
const buildCarrierTokens = (insurance: InsuranceSetting): string[] =>
    insurance.carriers.map((carrier) => {
        const plans = (carrier.plans || []).filter(Boolean);
        return plans.length ? `${carrier.name}: ${plans.join(', ')}` : `${carrier.name}`;
    });

interface InsuranceSummaryProps {
    insurance: InsuranceSetting;
    tooltipContent: string;
}

// 在 max-width 内尽量多展示 carrier，放不下的折叠成「and X more」（按 carrier 计数）
const InsuranceSummary: React.FC<InsuranceSummaryProps> = ({ insurance, tooltipContent }) => {
    const tokens = useMemo(() => buildCarrierTokens(insurance), [insurance]);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLSpanElement>(null);
    const [visibleCount, setVisibleCount] = useState(tokens.length);
    // 仅有一个 carrier 但它自身超过 max-width 被 ellipsis 截断时，没有「and X more」入口，
    // 需要把 tooltip 挂到整段文字上
    const [textClipped, setTextClipped] = useState(false);

    useLayoutEffect(() => {
        const wrapper = wrapperRef.current;
        const textEl = textRef.current;
        if (!wrapper || !textEl || tokens.length === 0) {
            return undefined;
        }

        const measure = () => {
            const maxWidth = wrapper.clientWidth;
            const ctx = getMeasureCtx();
            if (!maxWidth || !ctx) {
                return;
            }
            const cs = window.getComputedStyle(textEl);
            ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
            const widthOf = (str: string) => ctx.measureText(str).width;

            // 全部能放下就不折叠
            if (widthOf(tokens.join(', ')) <= maxWidth) {
                setVisibleCount(tokens.length);
                setTextClipped(false);
                return;
            }

            // 预留「and X more」的宽度（用最大可能的数字估算）
            const moreReserve = widthOf(` and ${tokens.length} more`);
            const avail = Math.max(0, maxWidth - moreReserve);
            let used = 0;
            let count = 0;
            for (let i = 0; i < tokens.length; i += 1) {
                const piece = i === 0 ? tokens[i] : `, ${tokens[i]}`;
                const w = widthOf(piece);
                if (used + w > avail) {
                    break;
                }
                used += w;
                count += 1;
            }
            // 至少展示一个 carrier，其超长部分由 ellipsis 兜底
            const finalCount = Math.max(1, count);
            setVisibleCount(finalCount);
            // 单个 carrier 文字本身就超出可用宽度（无 more 标签可点）时，标记需要文字 tooltip
            setTextClipped(finalCount === tokens.length && widthOf(tokens[finalCount - 1]) > maxWidth);
        };

        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(wrapper);
        return () => ro.disconnect();
    }, [tokens]);

    const moreCount = Math.max(0, tokens.length - visibleCount);
    const visibleText = tokens.slice(0, visibleCount).join(', ');

    const fullTooltip = (node: React.ReactNode) => (
        <Tooltip
            title={<div className={s.tooltipContent}>{tooltipContent}</div>}
            placement="top"
            overlayClassName={s.insuranceTooltip}
        >
            {node}
        </Tooltip>
    );

    return (
        <div className={s.insuranceTextWrapper} ref={wrapperRef}>
            {textClipped && moreCount === 0 ? (
                fullTooltip(
                    <span className={`${s.primaryText} ${s.tooltipTrigger}`} ref={textRef}>
                        {visibleText}
                    </span>,
                )
            ) : (
                <span className={s.primaryText} ref={textRef}>
                    {visibleText}
                </span>
            )}
            {moreCount > 0 &&
                fullTooltip(
                    <span className={`${s.secondaryText} ${s.tooltipTrigger}`}>{`and ${moreCount} more`}</span>,
                )}
        </div>
    );
};

const InsuranceTable = React.forwardRef<InsuranceTableRef, InsuranceTableProps>((props, ref) => {
    const {
        isEdit,
        selectedSpecialty,
        states,
        servicePriceConfig,
        className,
        paymentType = INSURANCE_TYPE_INSURANCE,
        onAddInsurance,
        onChange,
        isReview = false,
        initialInsuranceSettings,
        showSuperBillSupport,
        showPriorAuthorizationSupport,
        isOps,
        providerIdForOps,
        hideDuration,
    } = props;

    // 状态管理
    const [statesData, setStatesData] = useState<StateData[]>([]);
    const [insuranceSettings, setInsuranceSettings] = useState<
        Record<
            string,
            InsuranceSetting & {
                duration?: number;
                cashPrice?: number;
                insurancePay?: boolean;
                cashPay?: boolean;
                superBillSupport?: boolean | null;
                priorAuthorizationSupport?: boolean | null;
            }
        >
    >({});
    const [isInsuranceModalVisible, setIsInsuranceModalVisible] = useState(false);
    const [addInsuranceState, setAddInsuranceState] = useState<string>();
    const { scheduleToken: switchedScheduleToken, providerId: switchedProviderId } = useSwitchedProvider();
    const [getUser] = ProviderStore.useStore();
    const user = getUser('data');
    const providerId = switchedProviderId || (isOps ? providerIdForOps : user?.id);

    const [serviceTemplateData] = useKlarityServiceTemplate(isOps, providerId, switchedScheduleToken);
    const specialityInServiceTemplate = useMemo(
        () =>
            [...(serviceTemplateData?.recommend || []), ...(serviceTemplateData?.other || [])].find(
                (e) => e.specialtyName === selectedSpecialty,
            ),
        [serviceTemplateData, selectedSpecialty],
    );
    const serviceType = useMemo(
        () => specialityInServiceTemplate?.serversTypeNames?.find((e) => e.serviceType === EServiceType.INIT),
        [specialityInServiceTemplate],
    );

    // 使用验证 hook，使用 useMemo 缓存结果

    // 获取默认价格
    const getDefaultPrice = (stateCode: string): number | null => {
        if (!servicePriceConfig) return null;
        const config = servicePriceConfig.find((c) => c.state === stateCode && c.specialty === selectedSpecialty);
        return config?.price || null;
    };

    // 统一的状态更新函数
    const updateStateData = (stateCode: string, updates: Partial<StateData>) => {
        setStatesData((prevStates) => {
            const newStates = prevStates.map((state) => {
                if (state.code === stateCode) {
                    return { ...state, ...updates };
                }
                return state;
            });
            return newStates;
        });
        // 通知父组件状态已更新
        // setTimeout(() => onChange?.(), 1000);
    };

    // 保险设置更新
    const updateInsuranceSettings = (stateCode: string, settings: InsuranceSetting | null) => {
        setInsuranceSettings((prev) => {
            const next = { ...prev };
            if (settings === null) {
                delete next[stateCode];
            } else {
                next[stateCode] = settings;
            }
            return next;
        });
        // 通知父组件状态已更新
        // setTimeout(() => onChange?.(), 0);
    };

    const handleSuperBillSupportChange = (stateCode: string, checked: boolean) => {
        if (showSuperBillSupport) {
            updateStateData(stateCode, { superBillSupport: checked });
        } else {
            updateStateData(stateCode, { superBillSupport: null });
        }
    };

    const handlePriorAuthorizationSupportChange = (stateCode: string, checked: boolean) => {
        if (showPriorAuthorizationSupport) {
            updateStateData(stateCode, { priorAuthorizationSupport: checked });
        } else {
            updateStateData(stateCode, { priorAuthorizationSupport: null });
        }
    };

    // 处理保险开关变化
    const handleAcceptInsuranceChange = (stateCode: string, checked: boolean) => {
        // 1. 更新状态
        updateStateData(stateCode, { insurancePay: checked });
        // 2. 如果关闭，清除保险数据
        if (!checked) {
            updateInsuranceSettings(stateCode, null);
        } else if (!insuranceSettings[stateCode]) {
            // 如果开启，初始化一个空的保险设置（如果不存在）
            updateInsuranceSettings(stateCode, {
                carriers: [],
                otherCarriers: '',
                state: stateCode,
            });
        }
    };

    // 处理现金支付开关变化
    const handleAcceptCashChange = (stateCode: string, checked: boolean) => {
        // 1. 更新状态
        updateStateData(stateCode, {
            cashPay: checked,
            // 如果开启，恢复默认价格或保持当前价格
            cashPrice: checked ? getDefaultPrice(stateCode) : null,
        });
    };

    // 转换数据为 ServerKlarityService 格式
    const getServerKlarityServiceData = (ignoreValidation?: boolean): ServerKlarityService | null => {
        if (!providerId || !specialityInServiceTemplate || !serviceType) {
            return null;
        }
        // 1. 运行验证
        const validation = handleValidation(statesData, insuranceSettings, paymentType);
        if (!ignoreValidation && !validation.isValid) {
            validation.errors.forEach((error) => message.error(error));
            throw new Error(validation.errors[0]);
        }

        // 2. 转换基础数据
        const serviceStateItems: TServiceStateItem[] = statesData.map((state) => ({
            state: state.code,
            duration: state.duration,
            earning: 0, // 这些值需要从其他地方获取
            marketingMarkup: 0,
            platformFee: 0,
            patientCost: state.cashPay ? state.cashPrice || 0 : 0,
            practiceAddressIds: [], // 需要从其他地方获取
            teleHealth: true, // 需要根据实际情况设置
            inPerson: true,
            shipmentFee: 0,
            superBillSupport: state.superBillSupport,
            priorAuthorizationSupport: state.priorAuthorizationSupport,
            cashPay: state.cashPay,
            insurancePay: state.insurancePay,
        }));

        // 3. 转换保险数据（只包含开启了保险的州的数据，并平铺保险计划）
        const insurances = statesData
            .filter((state) => {
                if (paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE) {
                    return state.insurancePay;
                } else if (paymentType === INSURANCE_TYPE_INSURANCE) {
                    return true;
                } else {
                    return false;
                }
            })
            .reduce<
                Array<{
                    state: string;
                    carrier: string;
                    plan: string;
                    commercialPayerId?: string;
                    governmentPayerId?: string;
                }>
            >((acc, state) => {
                const setting = insuranceSettings[state.code];
                if (!setting) return acc;

                // 遍历每个保险公司及其计划
                setting.carriers.forEach((carrier) => {
                    if (carrier.plans?.length) {
                        carrier.plans.forEach((plan) =>
                            acc.push({
                                state: state.code,
                                carrier: carrier.name || '',
                                plan: plan || '',
                                commercialPayerId: carrier.commercialPayerId,
                                governmentPayerId: carrier.governmentPayerId,
                            }),
                        );
                    } else {
                        // 如果没有计划，只添加保险公司
                        acc.push({
                            state: state.code,
                            carrier: carrier.name || '',
                            plan: '',
                            commercialPayerId: carrier.commercialPayerId,
                            governmentPayerId: carrier.governmentPayerId,
                        });
                    }
                });

                return acc;
            }, []);

        // 4. 返回最终数据
        return {
            serviceId: null, // 新服务时为 null
            customizedServiceName: '',
            serviceSpecialtyName: selectedSpecialty || '',
            serviceSpecialtyId: specialityInServiceTemplate.id.toString(), // 需要从其他地方获取
            serviceTypeId: serviceType.id, // 需要从其他地方获取
            serviceType: EServiceType.INIT, // 需要从其他地方获取
            providerId,
            description: '',
            priceUpdateAt: new Date().toISOString(),
            disableFlag: false,
            serviceStateItems,
            patientGroupLimitations: [], // 需要从其他地方获取
            insurances,
            cashPay: statesData.some((state) => state.cashPay),
            insurancePay: statesData.some((state) => state.insurancePay),
        };
    };
    // 暴露方法给父组件
    // React.useImperativeHandle(ref, () => ({
    //     getServerKlarityServiceData,
    // }));

    useEffect(() => {
        try {
            const restultData = getServerKlarityServiceData(true);
            onChange?.(restultData);
        } catch (e) {
            console.error(e);
        }
    }, [statesData, insuranceSettings, specialityInServiceTemplate, serviceType]);

    // 初始化状态
    useEffect(() => {
        if (!selectedSpecialty) {
            return;
        }
        if (!states?.length) {
            setStatesData([]);
            return;
        }

        if (states && servicePriceConfig) {
            setStatesData((prevStates) => {
                const data = states.map((state) => {
                    const preState = prevStates?.find((c) => c.code === state);
                    const config = servicePriceConfig.filter((c) => c.state === state);
                    const specialtyTarget = config.find((c) => c.specialty === selectedSpecialty);

                    if (isEdit) {
                        return {
                            code: state,
                            duration: preState?.duration || specialtyTarget?.duration || 30,
                            cashPrice: preState?.cashPrice || specialtyTarget?.price || null,
                            insurancePay:
                                typeof preState?.insurancePay !== 'boolean'
                                    ? paymentType === INSURANCE_TYPE_INSURANCE ||
                                      paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE
                                    : preState?.insurancePay,
                            cashPay:
                                typeof preState?.cashPay !== 'boolean'
                                    ? paymentType === INSURANCE_TYPE_CASH ||
                                      paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE
                                    : preState?.cashPay,
                        };
                    } else {
                        return {
                            code: state,
                            duration: initialInsuranceSettings?.[state]?.duration || specialtyTarget?.duration || 30,
                            cashPrice: initialInsuranceSettings?.[state]?.cashPrice || specialtyTarget?.price || null,
                            insurancePay:
                                typeof initialInsuranceSettings?.[state]?.insurancePay !== 'boolean'
                                    ? paymentType === INSURANCE_TYPE_INSURANCE ||
                                      paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE
                                    : initialInsuranceSettings?.[state]?.insurancePay,
                            cashPay:
                                typeof initialInsuranceSettings?.[state]?.cashPay !== 'boolean'
                                    ? paymentType === INSURANCE_TYPE_CASH ||
                                      paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE
                                    : initialInsuranceSettings?.[state]?.cashPay,
                        };
                    }
                });

                return data;
            });
            // 延迟通知父组件，确保状态已更新
            // setTimeout(() => onChange?.(), 0);
        }

        if (!isEdit && initialInsuranceSettings) {
            setInsuranceSettings(initialInsuranceSettings);
        }
    }, [paymentType, selectedSpecialty, servicePriceConfig, states, initialInsuranceSettings, isEdit]);

    const handleDurationChange = (stateCode: string, value: number | null) => {
        if (value !== null) {
            updateStateData(stateCode, { duration: value });
        }
    };

    const handleCashPriceChange = (stateCode: string, value: number | null) => {
        if (value !== null) {
            updateStateData(stateCode, { cashPrice: value });
        }
    };

    const handleAddInsurance = (stateCode: string) => {
        setAddInsuranceState(stateCode);
        setIsInsuranceModalVisible(true);
        onAddInsurance?.(stateCode);
    };

    const handleInsuranceSave = (allInsuranceSettings: Record<string, InsuranceSetting>) => {
        setInsuranceSettings(allInsuranceSettings);
        setIsInsuranceModalVisible(false);
        // onChange?.();
    };

    // 生成tooltip内容的函数
    const generateTooltipContent = (insurance: InsuranceSetting) => {
        return insurance.carriers
            .map((carrier) => {
                const plansText = carrier.plans.map((plan) => `${plan} (Commercial plan)`).join(', ');
                return `${carrier.name}: ${plansText}.`;
            })
            .join('\n\n');
    };

    // 根据支付类型渲染不同的列组合
    const renderColumns = () => {
        const columns = [];

        // 州名列（始终显示）
        columns.push(
            <div key="states" className={s.column}>
                <div className={s.headerCell}>
                    <span className={s.headerText}>STATES</span>
                </div>
                <div className={s.divider} />
                {statesData?.map((state, index) => (
                    <React.Fragment key={index}>
                        <div className={s.dataCell}>
                            <span className={s.stateCode}>{state.code}</span>
                        </div>
                        {index < (statesData?.length || 0) - 1 && <div className={s.divider} />}
                    </React.Fragment>
                ))}
            </div>,
        );

        // 时长列（始终显示）
        if (!hideDuration) {
            columns.push(
                <div key="duration" className={s.column}>
                    <div className={s.headerCell}>
                        <span className={s.headerText}>
                            {paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE ? (
                                <>DURATION (MINS)</>
                            ) : (
                                'DURATION (MINS)'
                            )}
                        </span>
                        <Tooltip
                            overlayClassName={s.tipCard}
                            overlayInnerStyle={{ padding: '12px' }}
                            color="#FFFFFF"
                            title={
                                <div className={s.tipText}>
                                    Suggested duration: 30 minutes (based on our research). Duration can vary from 10 to
                                    120 minutes depending on your specific service needs.
                                </div>
                            }
                            trigger="hover"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                            >
                                <path
                                    d="M5.99582 6.98047C6.36261 6.20216 7.35231 5.64453 8.51566 5.64453C9.9913 5.64453 11.1875 6.54171 11.1875 7.64844C11.1875 8.58322 10.3341 9.36851 9.17977 9.58995C8.81747 9.65946 8.51566 9.9514 8.51566 10.3203M8.51562 12.3242H8.5223M14.5273 8.98438C14.5273 12.3046 11.8358 14.9961 8.51562 14.9961C5.19544 14.9961 2.50391 12.3046 2.50391 8.98438C2.50391 5.66419 5.19544 2.97266 8.51562 2.97266C11.8358 2.97266 14.5273 5.66419 14.5273 8.98438Z"
                                    stroke="#6B7280"
                                    strokeWidth="1.33594"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </Tooltip>
                    </div>
                    <div className={s.divider} />
                    {statesData?.map((state, index) => (
                        <React.Fragment key={state.code}>
                            <div className={s.dataCell}>
                                {isReview ? (
                                    <span className={s.durationText}>{state.duration}</span>
                                ) : (
                                    <InputNumber
                                        size="small"
                                        value={state.duration}
                                        onChange={(value) => handleDurationChange(state.code, value)}
                                        className={s.durationInput}
                                        min={1}
                                        max={999}
                                    />
                                )}
                            </div>
                            {index < (statesData?.length || 0) - 1 && <div className={s.divider} />}
                        </React.Fragment>
                    ))}
                </div>,
            );
        }

        // 根据支付类型添加相应的列
        if (paymentType === INSURANCE_TYPE_INSURANCE || paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE) {
            // Accept Insurance 列（仅在 both 模式下显示）
            if (paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE) {
                columns.push(
                    <div key="accept-insurance" className={s.column}>
                        <div className={s.headerCell}>
                            <span className={s.headerText}>
                                ACCEPT
                                <br />
                                INSURANCE
                            </span>
                            <Tooltip
                                overlayClassName={s.tipCard}
                                overlayInnerStyle={{ padding: '12px' }}
                                color="#FFFFFF"
                                title={
                                    <div className={s.tipText}>Patients can use their insurance for this service.</div>
                                }
                                trigger="hover"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="17"
                                    height="17"
                                    viewBox="0 0 17 17"
                                    fill="none"
                                >
                                    <path
                                        d="M5.99582 6.98047C6.36261 6.20216 7.35231 5.64453 8.51566 5.64453C9.9913 5.64453 11.1875 6.54171 11.1875 7.64844C11.1875 8.58322 10.3341 9.36851 9.17977 9.58995C8.81747 9.65946 8.51566 9.9514 8.51566 10.3203M8.51562 12.3242H8.5223M14.5273 8.98438C14.5273 12.3046 11.8358 14.9961 8.51562 14.9961C5.19544 14.9961 2.50391 12.3046 2.50391 8.98438C2.50391 5.66419 5.19544 2.97266 8.51562 2.97266C11.8358 2.97266 14.5273 5.66419 14.5273 8.98438Z"
                                        stroke="#6B7280"
                                        strokeWidth="1.33594"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Tooltip>
                        </div>
                        <div className={s.divider} />
                        {statesData?.map((state, index) => {
                            return (
                                <React.Fragment key={state.code}>
                                    <div className={s.dataCell}>
                                        <Switch
                                            checked={state.insurancePay}
                                            onChange={(checked) => handleAcceptInsuranceChange(state.code, checked)}
                                            className={s.acceptSwitch}
                                            disabled={isReview}
                                        />
                                    </div>
                                    {index < (statesData?.length || 0) - 1 && <div className={s.divider} />}
                                </React.Fragment>
                            );
                        })}
                    </div>,
                );
            }

            // Insurance Plans 列
            columns.push(
                <div key="insurance-plans" className={s.column}>
                    <div className={s.headerCell}>
                        <span className={s.headerText}>INSURANCE CARRIERS AND PLANS</span>
                        <Tooltip
                            overlayClassName={s.tipCard}
                            overlayInnerStyle={{ padding: '12px' }}
                            color="#FFFFFF"
                            title={
                                <div className={s.tipText}>
                                    The insurance carrier that provides coverage for the patient, and the specific plan
                                    if applicable.
                                </div>
                            }
                            trigger="hover"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                            >
                                <path
                                    d="M5.99582 6.98047C6.36261 6.20216 7.35231 5.64453 8.51566 5.64453C9.9913 5.64453 11.1875 6.54171 11.1875 7.64844C11.1875 8.58322 10.3341 9.36851 9.17977 9.58995C8.81747 9.65946 8.51566 9.9514 8.51566 10.3203M8.51562 12.3242H8.5223M14.5273 8.98438C14.5273 12.3046 11.8358 14.9961 8.51562 14.9961C5.19544 14.9961 2.50391 12.3046 2.50391 8.98438C2.50391 5.66419 5.19544 2.97266 8.51562 2.97266C11.8358 2.97266 14.5273 5.66419 14.5273 8.98438Z"
                                    stroke="#6B7280"
                                    strokeWidth="1.33594"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </Tooltip>
                    </div>
                    <div className={s.divider} />
                    {statesData?.map((state, index) => (
                        <React.Fragment key={state.code}>
                            <div className={s.dataCell}>
                                <div className={s.addButtonContainer}>
                                    {insuranceSettings[state.code] &&
                                    insuranceSettings[state.code].carriers &&
                                    insuranceSettings[state.code].carriers.length > 0 ? (
                                        <div className={s.insuranceSelectedInfo}>
                                            <InsuranceSummary
                                                insurance={insuranceSettings[state.code]}
                                                tooltipContent={generateTooltipContent(insuranceSettings[state.code])}
                                            />
                                            {!isReview && (
                                                <Button
                                                    onClick={() => handleAddInsurance(state.code)}
                                                    className={s.editButton}
                                                    disabled={
                                                        paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE &&
                                                        !state.insurancePay
                                                    }
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                        </div>
                                    ) : (
                                        <Button
                                            icon={<PlusOutlined />}
                                            onClick={() => handleAddInsurance(state.code)}
                                            disabled={
                                                (paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE &&
                                                    !state.insurancePay) ||
                                                isReview
                                            }
                                        >
                                            Add
                                        </Button>
                                    )}
                                </div>
                            </div>
                            {index < (statesData?.length || 0) - 1 && <div className={s.divider} />}
                        </React.Fragment>
                    ))}
                </div>,
            );
        }

        if (paymentType === INSURANCE_TYPE_CASH || paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE) {
            // Accept Cash 列（仅在 both 模式下显示）
            if (paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE) {
                columns.push(
                    <div key="accept-cash" className={s.column}>
                        <div className={s.headerCell}>
                            <span className={s.headerText}>ACCEPT CASH</span>
                            <Tooltip
                                overlayClassName={s.tipCard}
                                overlayInnerStyle={{ padding: '12px' }}
                                color="#FFFFFF"
                                title={
                                    <div className={s.tipText}>
                                        Patients can pay out of pocket at the price you set.
                                    </div>
                                }
                                trigger="hover"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="17"
                                    height="17"
                                    viewBox="0 0 17 17"
                                    fill="none"
                                >
                                    <path
                                        d="M5.99582 6.98047C6.36261 6.20216 7.35231 5.64453 8.51566 5.64453C9.9913 5.64453 11.1875 6.54171 11.1875 7.64844C11.1875 8.58322 10.3341 9.36851 9.17977 9.58995C8.81747 9.65946 8.51566 9.9514 8.51566 10.3203M8.51562 12.3242H8.5223M14.5273 8.98438C14.5273 12.3046 11.8358 14.9961 8.51562 14.9961C5.19544 14.9961 2.50391 12.3046 2.50391 8.98438C2.50391 5.66419 5.19544 2.97266 8.51562 2.97266C11.8358 2.97266 14.5273 5.66419 14.5273 8.98438Z"
                                        stroke="#6B7280"
                                        strokeWidth="1.33594"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Tooltip>
                        </div>
                        <div className={s.divider} />
                        {statesData?.map((state, index) => (
                            <React.Fragment key={state.code}>
                                <div className={s.dataCell}>
                                    <Switch
                                        checked={state.cashPay}
                                        onChange={(checked) => handleAcceptCashChange(state.code, checked)}
                                        className={s.acceptSwitch}
                                        disabled={isReview}
                                    />
                                </div>
                                {index < (statesData?.length || 0) - 1 && <div className={s.divider} />}
                            </React.Fragment>
                        ))}
                    </div>,
                );
            }

            // Patient Cash Pay Price 列
            columns.push(
                <div key="cash-price" className={s.column}>
                    <div className={s.headerCell}>
                        <span className={s.headerText}>PATIENT CASH PAY PRICE</span>
                        <Tooltip
                            overlayClassName={s.tipCard}
                            overlayInnerStyle={{ padding: '12px' }}
                            color="#FFFFFF"
                            title={
                                <div className={s.tipText}>
                                    The complete cost a patient will pay for your service package.
                                </div>
                            }
                            trigger="hover"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                            >
                                <path
                                    d="M5.99582 6.98047C6.36261 6.20216 7.35231 5.64453 8.51566 5.64453C9.9913 5.64453 11.1875 6.54171 11.1875 7.64844C11.1875 8.58322 10.3341 9.36851 9.17977 9.58995C8.81747 9.65946 8.51566 9.9514 8.51566 10.3203M8.51562 12.3242H8.5223M14.5273 8.98438C14.5273 12.3046 11.8358 14.9961 8.51562 14.9961C5.19544 14.9961 2.50391 12.3046 2.50391 8.98438C2.50391 5.66419 5.19544 2.97266 8.51562 2.97266C11.8358 2.97266 14.5273 5.66419 14.5273 8.98438Z"
                                    stroke="#6B7280"
                                    strokeWidth="1.33594"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </Tooltip>
                    </div>
                    <div className={s.divider} />
                    {statesData?.map((state, index) => (
                        <React.Fragment key={state.code}>
                            <div className={s.dataCell}>
                                {isReview ? (
                                    <span className={s.cashPriceText}>{state.cashPrice}</span>
                                ) : (
                                    <InputNumber
                                        size="small"
                                        value={state.cashPrice}
                                        onChange={(value) => handleCashPriceChange(state.code, value)}
                                        className={s.cashPriceInput}
                                        min={1}
                                        max={9999}
                                        disabled={paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE && !state.cashPay}
                                    />
                                )}
                            </div>
                            {index < (statesData?.length || 0) - 1 && <div className={s.divider} />}
                        </React.Fragment>
                    ))}
                </div>,
            );
        }

        if (
            showSuperBillSupport &&
            (paymentType === INSURANCE_TYPE_CASH || paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE)
        ) {
            columns.push(
                <div key="super-bill-support" className={s.column}>
                    <div className={s.headerCell}>
                        <span className={s.headerText}>SUPERBILL SUPPORT</span>
                        <Tooltip
                            overlayClassName={s.tipCard}
                            overlayInnerStyle={{ padding: '12px' }}
                            color="#FFFFFF"
                            title={
                                <div className={s.tipText}>
                                    Provides assistance with coding, documentation, and submission of superbills to
                                    maximize reimbursement and reduce claim denials for patients.
                                </div>
                            }
                            trigger="hover"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                            >
                                <path
                                    d="M5.99582 6.98047C6.36261 6.20216 7.35231 5.64453 8.51566 5.64453C9.9913 5.64453 11.1875 6.54171 11.1875 7.64844C11.1875 8.58322 10.3341 9.36851 9.17977 9.58995C8.81747 9.65946 8.51566 9.9514 8.51566 10.3203M8.51562 12.3242H8.5223M14.5273 8.98438C14.5273 12.3046 11.8358 14.9961 8.51562 14.9961C5.19544 14.9961 2.50391 12.3046 2.50391 8.98438C2.50391 5.66419 5.19544 2.97266 8.51562 2.97266C11.8358 2.97266 14.5273 5.66419 14.5273 8.98438Z"
                                    stroke="#6B7280"
                                    strokeWidth="1.33594"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </Tooltip>
                    </div>
                    <div className={s.divider} />
                    {statesData?.map((state, index) => (
                        <React.Fragment key={state.code}>
                            <div className={s.dataCell}>
                                <Switch
                                    checked={!!state.superBillSupport}
                                    onChange={(checked) => handleSuperBillSupportChange(state.code, checked)}
                                    className={s.acceptSwitch}
                                    disabled={isReview}
                                />
                            </div>
                            {index < (statesData?.length || 0) - 1 && <div className={s.divider} />}
                        </React.Fragment>
                    ))}
                </div>,
            );
        }

        if (
            showPriorAuthorizationSupport &&
            (paymentType === INSURANCE_TYPE_INSURANCE || paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE)
        ) {
            columns.push(
                <div key="prior-authorization-support" className={s.column}>
                    <div className={s.headerCell}>
                        <span className={s.headerText}>PRIOR AUTHORIZATION SUPPORT</span>
                        <Tooltip
                            overlayClassName={s.tipCard}
                            overlayInnerStyle={{ padding: '12px' }}
                            color="#FFFFFF"
                            title={
                                <div className={s.tipText}>
                                    Handles insurance approval processes for procedures, medications, and services,
                                    including documentation submission and appeal management for patients.
                                </div>
                            }
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="17"
                                height="17"
                                viewBox="0 0 17 17"
                                fill="none"
                            >
                                <path
                                    d="M5.99582 6.98047C6.36261 6.20216 7.35231 5.64453 8.51566 5.64453C9.9913 5.64453 11.1875 6.54171 11.1875 7.64844C11.1875 8.58322 10.3341 9.36851 9.17977 9.58995C8.81747 9.65946 8.51566 9.9514 8.51566 10.3203M8.51562 12.3242H8.5223M14.5273 8.98438C14.5273 12.3046 11.8358 14.9961 8.51562 14.9961C5.19544 14.9961 2.50391 12.3046 2.50391 8.98438C2.50391 5.66419 5.19544 2.97266 8.51562 2.97266C11.8358 2.97266 14.5273 5.66419 14.5273 8.98438Z"
                                    stroke="#6B7280"
                                    strokeWidth="1.33594"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </Tooltip>
                    </div>
                    <div className={s.divider} />
                    {statesData?.map((state, index) => (
                        <React.Fragment key={state.code}>
                            <div className={s.dataCell}>
                                <Switch
                                    checked={!!state.priorAuthorizationSupport}
                                    onChange={(checked) => handlePriorAuthorizationSupportChange(state.code, checked)}
                                    className={s.acceptSwitch}
                                    disabled={isReview}
                                />
                            </div>
                            {index < (statesData?.length || 0) - 1 && <div className={s.divider} />}
                        </React.Fragment>
                    ))}
                </div>,
            );
        }

        return columns;
    };

    // 暴露方法给父组件
    React.useImperativeHandle(ref, () => ({
        getServerKlarityServiceData,
    }));

    return (
        <div className={`${s.tableContainer} ${className || ''}`}>
            {(paymentType === INSURANCE_TYPE_INSURANCE || paymentType === INSURANCE_TYPE_CASH_AND_INSURANCE) && (
                <InsuranceCarrierModal
                    visible={isInsuranceModalVisible}
                    onCancel={() => setIsInsuranceModalVisible(false)}
                    onSave={handleInsuranceSave}
                    states={statesData?.map((state) => state.code) || []}
                    initialInsuranceSettings={insuranceSettings}
                    isOps={isOps}
                    openState={addInsuranceState}
                    showPayerId
                />
            )}
            <div className={s.table}>{renderColumns()}</div>
        </div>
    );
});

export default InsuranceTable;
